import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, useResource, errorText } from "@/lib/api";
import IncludedList from "@/components/IncludedList";
import RecordDialog from "@/components/RecordDialog";
import ProjectPicker from "@/components/projects/ProjectPicker";
import ProjectPanel from "@/components/projects/ProjectPanel";

const PROJECT_FIELDS = [
  { key: "name", label: "Project name", type: "text" },
  { key: "address", label: "Address", type: "text", optional: true },
  { key: "contact_name", label: "Contact person", type: "text", optional: true },
  { key: "contact_email", label: "Contact email", type: "text", optional: true },
];
const BLANK_PROJECT = { name: "", address: "", contact_name: "", contact_email: "" };

export default function MemoNotesPage() {
  const api = useApi();
  const { data } = useResource("/directors");
  const { data: projectData, reload: reloadProjects } = useResource("/projects");
  const { data: notes, reload: reloadNotes } = useResource("/memo_notes");
  const [pick, setPick] = useState("all");
  const [editingProject, setEditingProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tick, setTick] = useState(0);
  const directors = (data || []).filter((d) => d.active);
  const projects = projectData || [];
  const project = projects.find((p) => p.id === pick);
  const name = (id) => directors.find((d) => d.id === id)?.name || "All (split by share)";
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  useEffect(() => {
    if (!project) { setSummary(null); return; }
    api("get", `/projects/${project.id}/summary`).then(setSummary).catch(() => setSummary(null));
  }, [project?.id, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { reloadNotes(); setTick((t) => t + 1); };
  const saveProject = async (f) => {
    try {
      const body = { name: f.name, address: f.address || "", contact_name: f.contact_name || "", contact_email: f.contact_email || "" };
      const res = editingProject.id ? await api("put", `/projects/${editingProject.id}`, body) : await api("post", "/projects", body);
      toast.success("Project saved");
      setEditingProject(null);
      reloadProjects();
      setTick((t) => t + 1);
      if (res?.id) setPick(res.id);
    } catch (e) { return errorText(e); }
  };

  const fields = [
    { key: "label", label: "Label", type: "text" },
    { key: "amount", label: "Amount", type: "decimal" },
    { key: "project", label: "Project", type: "select",
      options: [{ value: "none", label: "No project" }, ...projects.map((p) => ({ value: p.id, label: p.name }))] },
    { key: "director", label: "Director", type: "select",
      options: [{ value: "none", label: "None — split by share" }, ...directors.map((d) => ({ value: d.id, label: d.name }))] },
    { key: "note", label: "Note", type: "text", optional: true },
    { key: "internal", label: "Internal — leave out of the project PDF", type: "switch" },
    { key: "include_in_settlement", label: "Include in settlement", type: "switch" },
  ];
  const filterRow = pick === "all" ? null : pick === "none" ? (r) => !r.project : (r) => r.project === pick;
  const defaultProject = project ? project.id : "none";

  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="memo-title">Memo notes</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">Free notes with an amount, grouped by project. Pick a project to see its total and download a summary PDF for the project contact.</p>
      {data && notes && projectData && (
        <>
          <ProjectPicker projects={projects} notes={notes} value={pick} onChange={setPick} onNew={() => setEditingProject({ ...BLANK_PROJECT })} />
          {project && (
            <ProjectPanel project={project} summary={summary} onEdit={() => setEditingProject({ ...BLANK_PROJECT, ...project })}
              onDeleted={() => { setPick("all"); reloadProjects(); }} />
          )}
          <IncludedList key={tick} table="memo_notes" prefix="memo" singular="Memo" fields={fields} filterRow={filterRow} onChange={refresh}
            columns={[{ key: "label", label: "Label" },
              { key: "project", label: "Project", render: (r) => r.project ? projectName(r.project) : <span className="text-muted-foreground">—</span> },
              { key: "director", label: "Director", render: (r) => name(r.director) },
              { key: "note", label: "Note", render: (r) => <>{r.note}{r.internal && <span className="ml-2 text-[10px] uppercase tracking-wider rounded-full border border-border px-1.5 py-0.5 text-muted-foreground" data-testid={`memo-internal-${r.id}`}>Internal</span>}</> }]}
            blank={{ label: "", amount: "", project: defaultProject, director: "none", note: "", internal: false, include_in_settlement: false }}
            toBody={(f) => ({ label: f.label, amount: f.amount, director: f.director && f.director !== "none" ? f.director : null,
              project: f.project && f.project !== "none" ? f.project : null, internal: !!f.internal, note: f.note || "", include_in_settlement: !!f.include_in_settlement })}
            toForm={(r) => ({ ...r, director: r.director || "none", project: r.project || "none", note: r.note || "", internal: !!r.internal })} label={(r) => r.label} />
        </>
      )}
      <RecordDialog open={!!editingProject} onOpenChange={(o) => !o && setEditingProject(null)} prefix="project-form"
        title={editingProject?.id ? "Edit project" : "New project"} fields={PROJECT_FIELDS} initial={editingProject || BLANK_PROJECT} onSubmit={saveProject} />
    </div>
  );
}
